import React, { useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getCategoryTabId, getGRETab, getTabs } from '@client/redux/modules/mylounge';
import { findActiveTab } from '@client/components/mylounge/banner/helpers';
import { scrollTo } from '@client/helpers';
import {
    CAMPAIGN_WRAPPER,
    M1_REBRANDING,
    THB_OUTER_WRAPPER,
} from '@client/components/mylounge/constants';
import { getElementRealHeight } from '@client/helpers/dom';
import { useConfig } from '@browsing/config';
import { trackSelectContent } from '@browsing/tracking';
import Tabs from './Tabs';

interface TabsProps {
    floating?: boolean;
    bannerTabsRef?: any;
}

export const TabsWrapper = ({ bannerTabsRef, floating }: TabsProps) => {
    const navigate = useNavigate();
    const tabs = useSelector(getTabs);
    const config = useConfig();
    const categoryTabId = useSelector(getCategoryTabId);
    const [activeIndex, setActiveIndex] = useState(findActiveTab(tabs, categoryTabId));
    const [lastIndex, setLastIndex] = useState(null);
    const isM1Rebranding = config.isEnabled(M1_REBRANDING);
    const greTab = useSelector(getGRETab);

    const changeActiveTab = useCallback(
        (newActiveIndex: number) =>
            navigate(`/event/${tabs?.[newActiveIndex]?.id}${window.location.search}`),
        [navigate, tabs],
    );

    const isSameTab = useCallback(
        (newActiveIndex: number) => newActiveIndex === activeIndex,
        [activeIndex],
    );

    const isGRETab = useCallback(
        (index: number) => {
            if (!greTab || !tabs || !tabs[index]) return false;
            return tabs[index].id === greTab.id;
        },
        [tabs, greTab],
    );

    const trackTabEvent = useCallback(
        (index: number) => {
            const { id } = tabs[index];

            trackSelectContent('category_tab', 'click', { contentId: id });
        },
        [tabs],
    );

    const handleTabChange = useCallback(
        (newActiveIndex: number) => {
            if (isSameTab(newActiveIndex)) {
                return;
            }

            trackTabEvent(newActiveIndex);
            changeActiveTab(newActiveIndex);

            // This is a temporary solution to scroll to the top of the page when the user clicks on the GRE tab
            // Currently, without this solution browser window scrolls down and miss the first campaign on GRE tab
            if (isGRETab(newActiveIndex)) {
                scrollTo({ top: 80 });
                return;
            }

            const wrapper = document.getElementById(CAMPAIGN_WRAPPER);
            const thbWrapper = document.getElementById(THB_OUTER_WRAPPER);

            if (wrapper) {
                if (isM1Rebranding) {
                    const height = getElementRealHeight(thbWrapper);
                    const { offsetTop } = wrapper;
                    const top = offsetTop - height;

                    scrollTo({ top });
                } else if (floating && bannerTabsRef && bannerTabsRef.current) {
                    const height = getElementRealHeight(bannerTabsRef.current);
                    const { offsetTop } = wrapper;

                    const top = offsetTop - height;

                    scrollTo({ top });
                }
            }
        },
        [isSameTab, trackTabEvent, changeActiveTab],
    );

    const handleTabClick = useCallback(
        (newActiveIndex: number) => handleTabChange(newActiveIndex),
        [handleTabChange],
    );

    useEffect(() => {
        const newActiveIndex = findActiveTab(tabs, categoryTabId);
        if (newActiveIndex !== activeIndex) {
            setActiveIndex(newActiveIndex);
            setLastIndex(activeIndex);
        }
    }, [activeIndex, categoryTabId, tabs]);

    return (
        <Tabs
            tabs={tabs}
            // @ts-ignore
            onTabClick={handleTabClick}
            activeIndex={activeIndex}
            lastIndex={lastIndex}
        />
    );
};
